import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  Chip,
  Paper,
  Stack,
  useTheme,
  alpha,
  OutlinedInput,
  Checkbox,
  ListItemText,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, People, CalendarToday, HealthAndSafety,
  FilterAltOutlined as FilterIcon,
  CloseRounded as CloseIcon,
  InsertChartOutlined as ChartIcon,
  PieChartOutline as PieIcon,
} from '@mui/icons-material';
import axios from 'axios';

import { registrationsAnalisis, registrationsAggregated } from '../BaseURL';

// Shared design tokens — kept local so we don't touch anything else in
// the system. These match the polish applied to the records table.
const PRIMARY      = '#059669';
const PRIMARY_DARK = '#047857';
const PRIMARY_BG   = 'rgba(5,150,105,0.08)';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';
const LABEL_SX     = {
  fontSize: '0.66rem',
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
};


// Get current year and month
const getCurrentYear = () => new Date().getFullYear().toString();
const getCurrentMonth = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[new Date().getMonth()];
};

const PatientRegistrationDashboard = () => {
  const theme = useTheme();
  const [selectedYear, setSelectedYear] = useState([getCurrentYear()]);
  const [selectedMonth, setSelectedMonth] = useState([getCurrentMonth()]);
  const [selectedWeek, setSelectedWeek] = useState([]);
  const [selectedInsurance, setSelectedInsurance] = useState([]);
  
  // API state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [stats, setStats] = useState({ total: 0, average: 0, peak: 0 });
  const [availableYears, setAvailableYears] = useState([]);
  const [availableInsuranceTypes, setAvailableInsuranceTypes] = useState([]);
  
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const weeks = ['1', '2', '3', '4'];
  
  // API configuration
  
  // Fetch metadata (years and insurance types) - only once on mount
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const response = await axios.get(registrationsAnalisis);
        
        if (response.data.success) {
          setAvailableYears(response.data.years || []);
          
          // Get insurance types sorted by count
          const insuranceData = response.data.insuranceTypes || [];
          setAvailableInsuranceTypes(insuranceData.map(item => item.name));
          
          // Auto-select top 4 insurance types by count
          const top4Insurance = insuranceData.slice(0, 4).map(item => item.name);
          setSelectedInsurance(top4Insurance);
          
          console.log('Auto-selected top 4 insurance types:', top4Insurance);
        }
      } catch (err) {
        console.error('Error fetching metadata:', err);
      }
    };
    
    fetchMetadata();
  }, []);
  
  // Fetch filtered data from backend whenever filters change
  const fetchFilteredData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add years
      selectedYear.forEach(year => params.append('year', year));
      
      // Add months
      selectedMonth.forEach(month => params.append('month', month));
      
      // Add weeks
      selectedWeek.forEach(week => params.append('week', week));
      
      // Add insurance types
      selectedInsurance.forEach(insurance => params.append('insurance', insurance));
      
      console.log('Fetching with params:', params.toString());
      
      // Fetch aggregated data from backend
      const response = await axios.get(`${registrationsAggregated}/?${params.toString()}`);
      
      if (response.data.success) {
        setChartData(response.data.chartData || []);
        setStats(response.data.stats || { total: 0, average: 0, peak: 0 });
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load registration data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, selectedWeek, selectedInsurance]);
  
  // Fetch data when filters change (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchFilteredData();
    }, 300); // 300ms debounce
    
    return () => clearTimeout(timer);
  }, [fetchFilteredData]);
  
  // Handle year selection (multi-select)
  const handleYearChange = (event) => {
    const { target: { value } } = event;
    setSelectedYear(typeof value === 'string' ? value.split(',') : value);
  };
  
  // Handle month selection (multi-select)
  const handleMonthChange = (event) => {
    const { target: { value } } = event;
    setSelectedMonth(typeof value === 'string' ? value.split(',') : value);
  };
  
  // Handle insurance type selection (multi-select)
  const handleInsuranceChange = (event) => {
    const { target: { value } } = event;
    setSelectedInsurance(typeof value === 'string' ? value.split(',') : value);
  };
  
  // Handle week selection (multi-select)
  const handleWeekChange = (event) => {
    const { target: { value } } = event;
    setSelectedWeek(typeof value === 'string' ? value.split(',') : value);
  };
  
  // Prepare display data for chart based on insurance selection
  const displayChartData = useMemo(() => {
    return chartData.map(item => {
      const dataPoint = {
        name: item.name,
        fullLabel: item.fullLabel
      };
      
      if (selectedInsurance.length === 0 || selectedInsurance.length === availableInsuranceTypes.length) {
        // Show all insurance types when none selected OR all selected
        dataPoint.patients = item.total;
        availableInsuranceTypes.forEach(ins => {
          dataPoint[ins] = item[ins] || 0;
        });
      } else if (selectedInsurance.length === 1) {
        // Show single insurance type
        dataPoint.patients = item[selectedInsurance[0]] || 0;
      } else {
        // Show multiple selected insurance types
        selectedInsurance.forEach(ins => {
          dataPoint[ins] = item[ins] || 0;
        });
        // Calculate total for selected insurance types
        dataPoint.Total = selectedInsurance.reduce((sum, ins) => sum + (item[ins] || 0), 0);
      }
      
      return dataPoint;
    });
  }, [chartData, selectedInsurance, availableInsuranceTypes]);
  
  // Color palette for insurance types
  const getInsuranceColor = (insurance, index) => {
    const colors = [
      '#2E7D32', '#1976D2', '#D32F2F', '#F57C00', '#7B1FA2',
      '#00796B', '#C2185B', '#5D4037', '#455A64', '#E64A19'
    ];
    return colors[index % colors.length];
  };
  
  const insuranceColors = useMemo(() => {
    const colors = {};
    availableInsuranceTypes.forEach((insurance, index) => {
      colors[insurance] = getInsuranceColor(insurance, index);
    });
    return colors;
  }, [availableInsuranceTypes]);
  
  const StatCard = ({ title, value, icon: Icon, color }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        bgcolor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 6px 16px ${alpha(color, 0.12)}`,
        },
        // Coloured accent strip on the left of every card so they stay
        // visually distinct without overpowering the white surface.
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0, top: 0, bottom: 0,
          width: 3,
          background: color,
        },
      }}
    >
      <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
        <Stack direction="row" alignItems="center" spacing={1.75}>
          <Box
            sx={{
              width: 42, height: 42, borderRadius: 1.5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha(color, 0.12),
              color,
            }}
          >
            <Icon sx={{ fontSize: 22 }} />
          </Box>
          <Box flex={1} sx={{ minWidth: 0 }}>
            <Typography sx={{ ...LABEL_SX, color: MUTED, mb: 0.25 }}>
              {title}
            </Typography>
            <Typography sx={{ fontWeight: 800, color: STRONG, fontSize: '1.6rem', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
              {value.toLocaleString()}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper 
          elevation={8}
          sx={{ 
            p: 2, 
            minWidth: 220,
            background: alpha(theme.palette.background.paper, 0.98),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: theme.palette.primary.main }}>
            {payload[0]?.payload?.fullLabel || label}
          </Typography>
          {payload.map((entry, index) => (
            <Stack key={index} direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 0.5 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box 
                  sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: entry.color,
                    boxShadow: `0 0 8px ${alpha(entry.color, 0.5)}`
                  }} 
                />
                <Typography variant="body2" color="text.secondary">
                  {entry.name}:
                </Typography>
              </Stack>
              <Typography variant="body2" fontWeight={600}>
                {entry.value}
              </Typography>
            </Stack>
          ))}
        </Paper>
      );
    }
    return null;
  };
  
  // Error state
  if (error) {
    return (
      <Box sx={{ 
        p: 3,
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}
        >
          {error}
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box sx={{
      p: 3,
      minHeight: '100vh',
      bgcolor: '#f8fafc',
    }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ ...LABEL_SX, color: PRIMARY, mb: 0.5 }}>
            Records · Analytics
          </Typography>
          <Typography sx={{ fontSize: '1.65rem', fontWeight: 800, color: STRONG, lineHeight: 1.15 }}>
            Patient Registration Trends
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: MUTED, mt: 0.5 }}>
            Monitor patient registration volume across periods and insurance partners.
          </Typography>
        </Box>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: MUTED }}>
            <CircularProgress size={14} sx={{ color: PRIMARY }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>Updating…</Typography>
          </Box>
        )}
      </Box>

      {/* Filters Section */}
      <Card
        elevation={0}
        sx={{
          mb: 2.5,
          p: 2.25,
          bgcolor: '#fff',
          border: `1px solid ${BORDER}`,
          borderRadius: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <FilterIcon sx={{ color: MUTED, fontSize: 18 }} />
          <Typography sx={{ ...LABEL_SX, color: MUTED }}>
            Filter Options
          </Typography>
        </Stack>
        
        <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>
          <Grid item xs={12} sm={6} md={3} size={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Year</InputLabel>
              <Select
                multiple
                value={selectedYear}
                onChange={handleYearChange}
                input={<OutlinedInput label="Year" />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return 'All Years';
                  }
                  return selected.join(', ');
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                  }
                }}
              >
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    <Checkbox checked={selectedYear.indexOf(year) > -1} />
                    <ListItemText primary={year} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3} size={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Month</InputLabel>
              <Select
                multiple
                value={selectedMonth}
                onChange={handleMonthChange}
                input={<OutlinedInput label="Month" />}
                disabled={selectedYear.length === 0}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return 'All Months';
                  }
                  return selected.join(', ');
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                  }
                }}
              >
                {months.map((month) => (
                  <MenuItem key={month} value={month}>
                    <Checkbox checked={selectedMonth.indexOf(month) > -1} />
                    <ListItemText primary={month} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3} size={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Week</InputLabel>
              <Select
                multiple
                value={selectedWeek}
                onChange={handleWeekChange}
                input={<OutlinedInput label="Week" />}
                disabled={selectedMonth.length === 0}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return 'All Weeks';
                  }
                  return selected.map(w => `W${w}`).join(', ');
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                  }
                }}
              >
                {weeks.map((week) => (
                  <MenuItem key={week} value={week}>
                    <Checkbox checked={selectedWeek.indexOf(week) > -1} />
                    <ListItemText primary={`Week ${week}`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3} size={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Insurance Type</InputLabel>
              <Select
                multiple
                value={selectedInsurance}
                onChange={handleInsuranceChange}
                input={<OutlinedInput label="Insurance Type" />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return 'All Insurance Types';
                  }
                  if (selected.length === availableInsuranceTypes.length) {
                    return 'All Insurance Types';
                  }
                  return `${selected.length} selected`;
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                  }
                }}
              >
                {/* Select All / Clear All Options */}
                <MenuItem 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (selectedInsurance.length === availableInsuranceTypes.length) {
                      setSelectedInsurance([]);
                    } else {
                      setSelectedInsurance([...availableInsuranceTypes]);
                    }
                  }}
                  sx={{ 
                    borderBottom: '1px solid',
                    borderColor: theme.palette.divider,
                    fontWeight: 600,
                    color: theme.palette.primary.main
                  }}
                >
                  {selectedInsurance.length === availableInsuranceTypes.length ? (
                    '✓ Clear All'
                  ) : (
                    '☐ Select All'
                  )}
                </MenuItem>
                
                {availableInsuranceTypes.map((insurance) => (
                  <MenuItem key={insurance} value={insurance}>
                    <Checkbox checked={selectedInsurance.indexOf(insurance) > -1} />
                    <ListItemText primary={insurance} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        {/* Active Filters Display */}
        {(selectedYear.length > 0 || selectedMonth.length > 0 || selectedWeek.length > 0 || selectedInsurance.length > 0) && (() => {
          // Shared style: outlined pill chips in the brand green; the
          // four filter kinds keep slight colour variations so the user
          // can tell year/month/week/insurance apart at a glance.
          const chipSx = (bg, fg) => ({
            height: 22,
            fontSize: '0.72rem',
            fontWeight: 700,
            bgcolor: bg,
            color: fg,
            border: `1px solid ${alpha(fg, 0.25)}`,
            '& .MuiChip-deleteIcon': { color: fg, fontSize: 14, '&:hover': { color: alpha(fg, 0.7) } },
          });
          return (
            <Box sx={{ mt: 1.75, pt: 1.5, borderTop: `1px dashed ${BORDER}`, display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography sx={{ ...LABEL_SX, mr: 0.5 }}>Active</Typography>
              {selectedYear.map((year) => (
                <Chip
                  key={`y-${year}`}
                  label={year}
                  onDelete={() => setSelectedYear(prev => prev.filter(y => y !== year))}
                  size="small"
                  deleteIcon={<CloseIcon />}
                  sx={chipSx(PRIMARY_BG, PRIMARY_DARK)}
                />
              ))}
              {selectedMonth.map((month) => (
                <Chip
                  key={`m-${month}`}
                  label={month}
                  onDelete={() => setSelectedMonth(prev => prev.filter(m => m !== month))}
                  size="small"
                  deleteIcon={<CloseIcon />}
                  sx={chipSx('rgba(14,165,233,0.10)', '#0369a1')}
                />
              ))}
              {selectedWeek.map((week) => (
                <Chip
                  key={`w-${week}`}
                  label={`Week ${week}`}
                  onDelete={() => setSelectedWeek(prev => prev.filter(w => w !== week))}
                  size="small"
                  deleteIcon={<CloseIcon />}
                  sx={chipSx('rgba(124,58,237,0.10)', '#6d28d9')}
                />
              ))}
              {selectedInsurance.map((insurance) => (
                <Chip
                  key={`i-${insurance}`}
                  label={insurance}
                  onDelete={() => setSelectedInsurance(prev => prev.filter(ins => ins !== insurance))}
                  size="small"
                  deleteIcon={<CloseIcon />}
                  sx={chipSx('rgba(245,158,11,0.10)', '#b45309')}
                />
              ))}
            </Box>
          );
        })()}
      </Card>
      
      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={4}>
          <StatCard
            title="Total Registrations"
            value={stats.total}
            icon={People}
            color={PRIMARY}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            title="Average per Period"
            value={stats.average}
            icon={TrendingUp}
            color="#0ea5e9"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            title="Peak Registration"
            value={stats.peak}
            icon={HealthAndSafety}
            color="#f59e0b"
          />
        </Grid>
      </Grid>
      
      {/* Chart Section */}
      <Card
        elevation={0}
        sx={{
          p: 2.25,
          bgcolor: '#fff',
          border: `1px solid ${BORDER}`,
          borderRadius: 2,
          position: 'relative',
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha('#ffffff', 0.7),
              zIndex: 10,
              borderRadius: 2,
            }}
          >
            <CircularProgress size={36} sx={{ color: PRIMARY }} />
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
            <ChartIcon sx={{ color: PRIMARY, fontSize: 16 }} />
            <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>
              Trend Chart
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: '0.98rem', fontWeight: 700, color: STRONG }}>
            Patient Registration Trends
          </Typography>
        </Box>
        
        {displayChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={450}>
            <LineChart 
              data={displayChartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <defs>
                {selectedInsurance.length === 0 || selectedInsurance.length === availableInsuranceTypes.length ? (
                  availableInsuranceTypes.map((insurance) => (
                    <linearGradient key={insurance} id={`color${insurance.replace(/\s+/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={insuranceColors[insurance]} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={insuranceColors[insurance]} stopOpacity={0.2}/>
                    </linearGradient>
                  ))
                ) : (
                  <linearGradient id="colorPatients" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#667eea" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#764ba2" stopOpacity={0.2}/>
                  </linearGradient>
                )}
              </defs>
              
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
              
              <XAxis 
                dataKey="name" 
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '12px', fontWeight: 500 }}
              />
              
              <YAxis 
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '12px', fontWeight: 500 }}
              />
              
              <Tooltip content={<CustomTooltip />} />
              
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="circle"
              />
              
              {selectedInsurance.length === 0 || selectedInsurance.length === availableInsuranceTypes.length ? (
                availableInsuranceTypes.map((insurance) => (
                  <Line
                    key={insurance}
                    type="monotone"
                    dataKey={insurance}
                    stroke={insuranceColors[insurance]}
                    strokeWidth={3}
                    dot={{ r: 5, fill: insuranceColors[insurance], strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 7, fill: insuranceColors[insurance], strokeWidth: 2, stroke: '#fff' }}
                    fill={`url(#color${insurance.replace(/\s+/g, '')})`}
                  />
                ))
              ) : selectedInsurance.length === 1 ? (
                <Line
                  type="monotone"
                  dataKey="patients"
                  stroke={insuranceColors[selectedInsurance[0]]}
                  strokeWidth={4}
                  dot={{ r: 6, fill: insuranceColors[selectedInsurance[0]], strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 8, fill: insuranceColors[selectedInsurance[0]], strokeWidth: 2, stroke: '#fff' }}
                  fill="url(#colorPatients)"
                  name={selectedInsurance[0]}
                />
              ) : (
                <>
                  {selectedInsurance.map((insurance) => (
                    <Line
                      key={insurance}
                      type="monotone"
                      dataKey={insurance}
                      stroke={insuranceColors[insurance]}
                      strokeWidth={3}
                      dot={{ r: 5, fill: insuranceColors[insurance], strokeWidth: 2, stroke: '#fff' }}
                      activeDot={{ r: 7, fill: insuranceColors[insurance], strokeWidth: 2, stroke: '#fff' }}
                      fill={`url(#color${insurance.replace(/\s+/g, '')})`}
                    />
                  ))}
                  <Line
                    type="monotone"
                    dataKey="Total"
                    stroke="#667eea"
                    strokeWidth={4}
                    strokeDasharray="5 5"
                    dot={{ r: 6, fill: '#667eea', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 8, fill: '#667eea', strokeWidth: 2, stroke: '#fff' }}
                    name="Total Selected"
                  />
                </>
              )}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              flexDirection: 'column', gap: 0.5,
              height: 360, borderRadius: 1.5,
              border: `1px dashed ${BORDER}`,
              bgcolor: '#f8fafc',
            }}
          >
            <ChartIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 0.5 }} />
            <Typography sx={{ fontWeight: 700, color: STRONG, fontSize: '0.95rem' }}>
              No data for the selected filters
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: '0.8rem' }}>
              Try adjusting the period or insurance selection.
            </Typography>
          </Box>
        )}
      </Card>

      {/* Insurance Breakdown Card */}
      {(selectedInsurance.length === 0 || selectedInsurance.length === availableInsuranceTypes.length) && displayChartData.length > 0 && (
        <Card
          elevation={0}
          sx={{
            mt: 2.5,
            p: 2.25,
            bgcolor: '#fff',
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
          }}
        >
          <Box sx={{ mb: 1.75 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
              <PieIcon sx={{ color: PRIMARY, fontSize: 16 }} />
              <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>
                Distribution
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: '0.98rem', fontWeight: 700, color: STRONG }}>
              Insurance Type Distribution
            </Typography>
          </Box>

          <Grid container spacing={1.5}>
            {availableInsuranceTypes.map((insurance) => {
              const total = displayChartData.reduce((sum, d) => sum + (d[insurance] || 0), 0);
              const c = insuranceColors[insurance];
              return (
                <Grid item xs={6} sm={4} md={2.4} key={insurance}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: '#fff',
                      border: `1px solid ${BORDER}`,
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 16px ${alpha(c, 0.12)}`,
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: c,
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 800, color: STRONG,
                        fontSize: '1.4rem', lineHeight: 1.1,
                        fontVariantNumeric: 'tabular-nums', mb: 0.25,
                      }}
                    >
                      {total.toLocaleString()}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.72rem', fontWeight: 600,
                        color: MUTED, textTransform: 'uppercase', letterSpacing: 0.3,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}
                      title={insurance}
                    >
                      {insurance}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Card>
      )}
    </Box>
  );
};

export default PatientRegistrationDashboard;